import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const AddressBook = ({ user }) => {
  // 1. ESTADOS
  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clienteIdABorrar, setClienteIdABorrar] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [formData, setFormData] = useState(null);

  const effectiveUser = user || (() => {
    try {
      const saved = sessionStorage.getItem('tabUserData') || localStorage.getItem('userData');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  const activeUserCode = String(effectiveUser?.c_usuar || effectiveUser?.nombre || '').trim().toUpperCase();
  const activeCompanyId = Number(
    effectiveUser?.empresa_activa?.c_nuges ||
    effectiveUser?.empresa_activa?.id ||
    sessionStorage.getItem('tabCompanyId') ||
    localStorage.getItem('lastCompanyId') ||
    0
  );

  const fetchClientes = useCallback(async () => {
    if (!activeUserCode || !activeCompanyId) {
      setClientes([]);
      return;
    }
    try {
      const response = await axios.get('/api/contactos/', {
        params: { c_usuar: activeUserCode, c_nuges: activeCompanyId },
      });
      setClientes(response.data || []);
    } catch (error) {
      console.error("Error al obtener los clientes:", error);
    }
  }, [activeUserCode, activeCompanyId]);

  // Cargar contactos desde el backend al montar o cambiar de usuario/empresa
  useEffect(() => {
    setFormData(null);
    setIsEditing(false);
    setIsNewRecord(false);
    fetchClientes();
  }, [fetchClientes]);

  // 2. LÓGICA
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const calcularEdad = (fecha) => {
    if (!fecha) return 0;
    const hoy = new Date();
    const cumple = new Date(fecha);
    let edad = hoy.getFullYear() - cumple.getFullYear();
    const m = hoy.getMonth() - cumple.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < cumple.getDate())) edad--;
    return edad >= 0 ? edad : 0;
  };

  const clientesFiltrados = clientes
    .filter(c => 
      c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cuit.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) || // Lógica buscador Email
      c.id.toString().includes(searchTerm)
    )
    .sort((a, b) => {
      if (sortConfig.key === 'nombre') {
        return sortConfig.direction === 'asc' ? a.nombre.localeCompare(b.nombre) : b.nombre.localeCompare(a.nombre);
      }
      return sortConfig.direction === 'asc' ? a.id - b.id : b.id - a.id;
    });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = clientesFiltrados.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(clientesFiltrados.length / recordsPerPage);

  const handleNuevoRegistro = () => {
    // Ya no seteamos el ID, lo generará la base de datos automáticamente
    // Usamos los nuevos identificadores con prefijo para el estado del formulario
    setFormData({
      addressbook_nombre: "",
      addressbook_cuit: "",
      addressbook_fechaNacimiento: "",
      addressbook_direccion: "",
      addressbook_telefonos: [],
      addressbook_emails: [],
      addressbook_zona: "",
      addressbook_notas: "",
      deletedTelefonos: [],
      deletedEmails: []
    });
    setIsNewRecord(true); 
    setIsEditing(true); 
  };

  const handleVerDetalles = (cliente) => {
    setFormData({
      id: cliente.id,
      addressbook_nombre: cliente.nombre || "",
      addressbook_cuit: cliente.cuit || "",
      addressbook_fechaNacimiento: cliente.fechaNacimiento || "",
      addressbook_direccion: cliente.direccion || "",
      addressbook_zona: cliente.zona || "",
      addressbook_notas: cliente.notas || "",
      addressbook_telefonos: cliente.telefonos ? cliente.telefonos.map(t => ({...t})) : [],
      addressbook_emails: cliente.emails ? cliente.emails.map(e => ({...e})) : [],
      deletedTelefonos: [],
      deletedEmails: []
    }); 
    setIsNewRecord(false); 
    setIsEditing(false); 
  };

  const handleSave = async () => {
    if (!formData.addressbook_nombre?.trim()) {
      Swal.fire({
        title: 'Campo Obligatorio',
        text: 'El nombre es obligatorio',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    // Validar que no haya teléfonos vacíos
    if (formData.addressbook_telefonos?.some(t => !t.numero.trim())) {
      Swal.fire({
        title: 'Campos Incompletos',
        text: 'Hay campos de teléfono vacíos. Por favor completalos o eliminalos usando el botón de basura.',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
      return;
    }
    // Validar que no haya emails vacíos
    if (formData.addressbook_emails?.some(e => !e.email.trim())) {
      Swal.fire({
        title: 'Campos Incompletos',
        text: 'Hay campos de email vacíos. Por favor completalos o eliminalos usando el botón de basura.',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    // Mapeamos los datos del formulario a la estructura que espera el backend
    const payload = {
      nombre: formData.addressbook_nombre,
      cuit: formData.addressbook_cuit,
      fechaNacimiento: formData.addressbook_fechaNacimiento || null,
      direccion: formData.addressbook_direccion,
      zona: formData.addressbook_zona,
      notas: formData.addressbook_notas,
      c_usuar: activeUserCode,
      c_nuges: activeCompanyId,
    };

    // Si no es un registro nuevo, incluimos el ID para la actualización
    if (!isNewRecord) {
      payload.id = formData.id;
    }

    try {
      let currentContactId = formData.id;

      if (isNewRecord) {
        const response = await axios.post('/api/contactos/', payload, {
          params: { c_usuar: activeUserCode, c_nuges: activeCompanyId },
        });
        currentContactId = response.data.id;
      } else {
        await axios.put(`/api/contactos/${currentContactId}/`, payload, {
          params: { c_usuar: activeUserCode, c_nuges: activeCompanyId },
        });
      }
      
      // Sincronizar Teléfonos
      for (let tel of formData.addressbook_telefonos) {
        if (tel.id) {
          await axios.put(`/api/telefonos/${tel.id}/`, { contacto: currentContactId, numero: tel.numero, descripcion: tel.descripcion });
        } else {
          await axios.post('/api/telefonos/', { contacto: currentContactId, numero: tel.numero, descripcion: tel.descripcion });
        }
      }
      for (let id of formData.deletedTelefonos) { await axios.delete(`/api/telefonos/${id}/`); }

      // Sincronizar Emails
      for (let email of formData.addressbook_emails) {
        if (email.id) {
          await axios.put(`/api/emails/${email.id}/`, { contacto: currentContactId, email: email.email, descripcion: email.descripcion });
        } else {
          await axios.post('/api/emails/', { contacto: currentContactId, email: email.email, descripcion: email.descripcion });
        }
      }
      for (let id of formData.deletedEmails) { await axios.delete(`/api/emails/${id}/`); }

      // Refrescar y actualizar vista para ver los nuevos IDs generados por el backend
      const res = await axios.get('/api/contactos/', {
        params: { c_usuar: activeUserCode, c_nuges: activeCompanyId },
      });
      setClientes(res.data);
      const updatedContacto = res.data.find(c => c.id === currentContactId);
      if (updatedContacto) handleVerDetalles(updatedContacto);

    } catch (error) {
      console.error("Error al guardar:", error.response?.data || error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
      Swal.fire({
        title: 'Error',
        text: `Ocurrió un error al guardar el contacto:\n${errorMsg}`,
        icon: 'error',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const handleCerrarDetalles = () => {
    setFormData(null);
    setIsEditing(false);
    setIsNewRecord(false);
  };

  // --- LÓGICA DE TABLITAS DE TELÉFONOS Y EMAILS ---
  const handleAddTelefono = () => {
    setFormData({ 
      ...formData, 
      addressbook_telefonos: [...(formData.addressbook_telefonos || []), { id: null, numero: "", descripcion: "" }] 
    });
  };

  const handleUpdateTelefono = (index, field, value) => {
    const nuevos = [...formData.addressbook_telefonos];
    nuevos[index][field] = value;
    setFormData({ ...formData, addressbook_telefonos: nuevos });
  };

  const handleDeleteTelefono = (index) => {
    const aBorrar = formData.addressbook_telefonos[index];
    const nuevos = formData.addressbook_telefonos.filter((_, i) => i !== index);
    const newDeleted = aBorrar.id ? [...formData.deletedTelefonos, aBorrar.id] : formData.deletedTelefonos;
    setFormData({ ...formData, addressbook_telefonos: nuevos, deletedTelefonos: newDeleted });
  };

  const handleAddEmail = () => {
    setFormData({ 
      ...formData, 
      addressbook_emails: [...(formData.addressbook_emails || []), { id: null, email: "", descripcion: "" }] 
    });
  };

  const handleUpdateEmail = (index, field, value) => {
    const nuevos = [...formData.addressbook_emails];
    nuevos[index][field] = value;
    setFormData({ ...formData, addressbook_emails: nuevos });
  };

  const handleDeleteEmail = (index) => {
    const aBorrar = formData.addressbook_emails[index];
    const nuevos = formData.addressbook_emails.filter((_, i) => i !== index);
    const newDeleted = aBorrar.id ? [...formData.deletedEmails, aBorrar.id] : formData.deletedEmails;
    setFormData({ ...formData, addressbook_emails: nuevos, deletedEmails: newDeleted });
  };
  // ------------------------------------------------

  const handleCancelar = () => {
    if (isNewRecord) {
      handleCerrarDetalles();
    } else {
      // Buscamos el cliente original y usamos la misma lógica que "Ver Detalles" para resetear el form
      const originalCliente = clientes.find(c => c.id === formData.id);
      handleVerDetalles(originalCliente);
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/contactos/${clienteIdABorrar}/`, {
        params: { c_usuar: activeUserCode, c_nuges: activeCompanyId },
      });
      setClientes(clientes.filter(c => c.id !== clienteIdABorrar));
      if (formData && formData.id === clienteIdABorrar) {
        setFormData(null);
      }
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error al eliminar:", error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al eliminar el contacto.',
        icon: 'error',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  return (
    <div className="container-fluid mt-4 pb-5 text-start px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><i className="bi bi-people me-2"></i>Agenda de Contactos</h2>
        <button className="btn btn-primary shadow-sm" onClick={handleNuevoRegistro}>
          <i className="bi bi-person-plus me-2"></i>Agregar Registro
        </button>
      </div>

      {/* BUSCADOR Y ORDEN (Movido fuera de la tarjeta para más espacio) */}
      <div className="row mb-4">
        <div className="col-md-10 col-lg-8 col-xl-6 d-flex flex-column flex-sm-row gap-3">
          <div className="input-group shadow-sm">
            <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
            <input type="text" className="form-control" placeholder="Buscar por Nombre, CUIT, Email o ID..." value={searchTerm} 
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
          <div className="d-flex gap-2 flex-shrink-0">
            <button 
              type="button"
              className={`btn px-3 text-nowrap shadow-sm ${sortConfig.key === 'nombre' ? 'btn-primary' : 'btn-white bg-white text-primary border'}`} 
              style={{ borderRadius: '8px' }}
              onClick={() => handleSort('nombre')} 
              title="Ordenar por Nombre"
            >
              <i className={`bi bi-sort-alpha-${sortConfig.key === 'nombre' && sortConfig.direction === 'desc' ? 'up' : 'down'} me-1`}></i> A-Z
            </button>
            <button 
              type="button"
              className={`btn px-3 text-nowrap shadow-sm ${sortConfig.key === 'id' ? 'btn-primary' : 'btn-white bg-white text-primary border'}`} 
              style={{ borderRadius: '8px' }}
              onClick={() => handleSort('id')} 
              title="Ordenar por ID"
            >
              <i className={`bi bi-sort-numeric-${sortConfig.key === 'id' && sortConfig.direction === 'desc' ? 'up' : 'down'} me-1`}></i> ID
            </button>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* COLUMNA IZQUIERDA: LISTADO */}
        <div className="col-lg-5 col-xl-4">
          <div className="card shadow-sm border-0 h-100 d-flex flex-column">
            <div className="card-body">
              
              {/* TABLA DE CONTACTOS REDUCIDA */}
              <div className="table-responsive" style={{ flexGrow: 1 }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '60px' }}>ID</th>
                      <th>Nombre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.length > 0 ? (
                      currentRecords.map((cliente) => (
                        <tr 
                          key={cliente.id} 
                          onClick={() => handleVerDetalles(cliente)}
                          className={formData?.id === cliente.id ? 'table-primary' : ''}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="text-muted fw-bold small">#{cliente.id}</td>
                          <td className="fw-bold">{cliente.nombre}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center py-4 text-muted">
                          <i className="bi bi-person-x fs-3 d-block mb-2"></i>
                          No se encontraron registros
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINACIÓN COMPACTA */}
              <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                <select className="form-select form-select-sm shadow-sm w-auto" value={recordsPerPage}
                  onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                
                {totalPages > 1 && (
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>«</button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">{currentPage} / {totalPages}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>»</button>
                    </li>
                  </ul>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA: FICHA DEL CONTACTO (Reemplaza el Modal) */}
        <div className="col-lg-7 col-xl-8">
          {formData ? (
            <div className="card shadow-sm border-0 h-100">
              <div className={`card-header d-flex justify-content-between align-items-center py-3 ${isNewRecord ? 'bg-success text-white' : isEditing ? 'bg-warning text-dark' : 'bg-primary text-white'}`}>
                <h5 className="mb-0 fw-bold">
                  {isNewRecord ? 'Nuevo Registro' : isEditing ? 'Modificando Ficha' : 'Ficha de Contacto'} 
                  {!isNewRecord && <span className="badge bg-dark ms-2">ID: {formData.id}</span>}
                </h5>
                <button type="button" className={`btn-close ${isEditing && !isNewRecord ? '' : 'btn-close-white'}`} onClick={handleCerrarDetalles} aria-label="Cerrar"></button>
              </div>
              
              <div className="card-body p-4 bg-light overflow-auto">
                <div className="row g-3">
                  <div className="col-md-4 d-flex align-items-center">
                    <label className="text-muted small fw-bold text-end me-2 text-nowrap" style={{ width: '85px', flexShrink: 0 }}>ID</label>
                    <input type="text" className="form-control bg-white text-center fw-bold" value={formData.id || "NUEVO"} readOnly />
                  </div>
                  <div className="col-md-8 d-flex align-items-center">
                    <label className="text-muted small fw-bold text-end me-2 text-nowrap" style={{ width: '85px', flexShrink: 0 }}>Nombre</label>
                    <input name="addressbook_nombre" type="text" className="form-control" value={formData.addressbook_nombre || ""} onChange={(e) => setFormData({...formData, addressbook_nombre: e.target.value})} readOnly={!isEditing} />
                  </div>

                  <div className="col-md-6 d-flex align-items-center">
                    <label className="text-primary small fw-bold text-end me-2 text-nowrap" style={{ width: '85px', flexShrink: 0 }}><i className="bi bi-geo-alt me-1"></i>Dirección</label>
                    <input type="text" className="form-control" value={formData.addressbook_direccion || ""} onChange={(e) => setFormData({...formData, addressbook_direccion: e.target.value})} readOnly={!isEditing} />
                  </div>
                  <div className="col-md-6 d-flex align-items-center">
                    <label className="text-secondary small fw-bold text-end me-2 text-nowrap" style={{ width: '85px', flexShrink: 0 }}><i className="bi bi-map me-1"></i>Zona</label>
                    <input type="text" className="form-control" value={formData.addressbook_zona || ""} onChange={(e) => setFormData({...formData, addressbook_zona: e.target.value})} readOnly={!isEditing} />
                  </div>

                  <div className="col-md-5 d-flex align-items-center">
                    <label className="text-muted small fw-bold text-end me-2 text-nowrap" style={{ width: '85px', flexShrink: 0 }}>DNI / CUIT</label>
                    <input name="addressbook_cuit" type="text" className="form-control" value={formData.addressbook_cuit || ""} onChange={(e) => setFormData({...formData, addressbook_cuit: e.target.value})} readOnly={!isEditing} />
                  </div>
                  <div className="col-md-7 d-flex align-items-center">
                    <label className="text-muted small fw-bold text-end me-2 text-nowrap" style={{ width: '85px', flexShrink: 0 }}>F. Nac.</label>
                    <input name="addressbook_fechaNacimiento" type="date" className="form-control px-2" value={formData.addressbook_fechaNacimiento || ""} onChange={(e) => setFormData({...formData, addressbook_fechaNacimiento: e.target.value})} readOnly={!isEditing} />
                    <label className="text-muted small fw-bold ms-3 me-2 text-nowrap">Edad</label>
                    <input type="text" className="form-control bg-light text-center fw-bold px-1" style={{ width: '60px' }} value={calcularEdad(formData.addressbook_fechaNacimiento)} readOnly />
                  </div>

                  {/* TABLITA TELÉFONOS */}
                  <div className="col-md-6 mt-4">
                    <div className="d-flex justify-content-between align-items-end mb-2 ms-2">
                      <label className="text-success small fw-bold text-nowrap"><i className="bi bi-telephone me-1"></i>Teléfonos</label>
                      {isEditing && (
                        <button type="button" className="btn btn-sm btn-outline-success py-0 px-2" onClick={handleAddTelefono} title="Agregar Teléfono">
                          <i className="bi bi-plus-lg"></i>
                        </button>
                      )}
                    </div>
                    <div className="table-responsive border border-success-subtle rounded bg-white">
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="ps-3 text-secondary small" style={{ width: '45%' }}>Número</th>
                            <th className="text-secondary small">Descripción</th>
                            {isEditing && <th style={{ width: '70px' }}></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.addressbook_telefonos || []).length > 0 ? formData.addressbook_telefonos.map((tel, idx) => (
                            <tr key={idx}>
                              <td className="align-middle ps-3 text-break fw-semibold">
                                {isEditing ? (
                                  <input type="text" className="form-control form-control-sm" placeholder="Ej: 11-1234-5678" value={tel.numero} onChange={(e) => handleUpdateTelefono(idx, 'numero', e.target.value)} />
                                ) : tel.numero}
                              </td>
                              <td className="align-middle text-muted small">
                                {isEditing ? (
                                  <input type="text" className="form-control form-control-sm" placeholder="Ej: Casa, Trabajo..." value={tel.descripcion} onChange={(e) => handleUpdateTelefono(idx, 'descripcion', e.target.value)} />
                                ) : tel.descripcion}
                              </td>
                              {isEditing && (
                                <td className="text-end align-middle pe-2">
                                  <button type="button" className="btn btn-sm text-danger p-0" title="Eliminar" onClick={() => handleDeleteTelefono(idx)}><i className="bi bi-trash"></i></button>
                                </td>
                              )}
                            </tr>
                          )) : <tr><td className="text-muted small text-center py-2" colSpan={isEditing ? 3 : 2}>Sin teléfonos registrados</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* TABLITA EMAILS */}
                  <div className="col-md-6 mt-4">
                    <div className="d-flex justify-content-between align-items-end mb-2 ms-2">
                      <label className="text-info small fw-bold text-nowrap"><i className="bi bi-envelope me-1"></i>Emails</label>
                      {isEditing && (
                        <button type="button" className="btn btn-sm btn-outline-info py-0 px-2" onClick={handleAddEmail} title="Agregar Email">
                          <i className="bi bi-plus-lg"></i>
                        </button>
                      )}
                    </div>
                    <div className="table-responsive border border-info-subtle rounded bg-white">
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="ps-3 text-secondary small" style={{ width: '45%' }}>Email</th>
                            <th className="text-secondary small">Descripción</th>
                            {isEditing && <th style={{ width: '70px' }}></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.addressbook_emails || []).length > 0 ? formData.addressbook_emails.map((email, idx) => (
                            <tr key={idx}>
                              <td className="align-middle ps-3 text-break fw-semibold">
                                {isEditing ? (
                                  <input type="email" className="form-control form-control-sm" placeholder="ejemplo@correo.com" value={email.email} onChange={(e) => handleUpdateEmail(idx, 'email', e.target.value)} />
                                ) : email.email}
                              </td>
                              <td className="align-middle text-muted small">
                                {isEditing ? (
                                  <input type="text" className="form-control form-control-sm" placeholder="Ej: Laboral, Personal..." value={email.descripcion} onChange={(e) => handleUpdateEmail(idx, 'descripcion', e.target.value)} />
                                ) : email.descripcion}
                              </td>
                              {isEditing && (
                                <td className="text-end align-middle pe-2">
                                  <button type="button" className="btn btn-sm text-danger p-0" title="Eliminar" onClick={() => handleDeleteEmail(idx)}><i className="bi bi-trash"></i></button>
                                </td>
                              )}
                            </tr>
                          )) : <tr><td className="text-muted small text-center py-2" colSpan={isEditing ? 3 : 2}>Sin emails registrados</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-12 d-flex mt-4">
                    <label className="text-warning-emphasis small fw-bold text-end me-2 text-nowrap mt-2" style={{ width: '85px', flexShrink: 0 }}><i className="bi bi-sticky me-1"></i>Notas</label>
                    <textarea className="form-control bg-warning-subtle border-warning border-start border-4 shadow-sm" rows="3" value={formData.addressbook_notas || ""} onChange={(e) => setFormData({...formData, addressbook_notas: e.target.value})} readOnly={!isEditing}></textarea>
                  </div>
                </div>
              </div>
              
              <div className="card-footer bg-white d-flex justify-content-between py-3">
                <div>
                  {!isEditing && (
                    <button className="btn btn-outline-danger px-4" onClick={() => { setClienteIdABorrar(formData.id); setShowDeleteModal(true); }}>
                      <i className="bi bi-trash me-2"></i>Eliminar
                    </button>
                  )}
                </div>
                <div>
                  {isEditing ? (
                    <>
                      <button className="btn btn-outline-secondary me-2 px-4" onClick={handleCancelar}>Cancelar</button>
                      <button className="btn btn-success px-5" onClick={handleSave}><i className="bi bi-check-lg me-2"></i>{isNewRecord ? 'Crear Contacto' : 'Guardar Cambios'}</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-outline-secondary me-2 px-4" onClick={handleCerrarDetalles}>Cerrar</button>
                      <button className="btn btn-warning px-5" onClick={() => setIsEditing(true)}><i className="bi bi-pencil me-2"></i>Modificar</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted bg-white rounded-3 shadow-sm border p-5">
              <i className="bi bi-person-badge display-1 mb-3 text-secondary opacity-50"></i>
              <h4 className="fw-bold">Seleccioná un contacto</h4>
              <p className="text-center">Hacé clic en un contacto de la lista a la izquierda para ver o editar sus detalles, o utilizá el botón <strong>Agregar Registro</strong> para crear uno nuevo.</p>
            </div>
          )}
        </div>
      </div>

      {/* MODAL BORRADO SIGUE IGUAL */}
      {showDeleteModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered text-center">
            <div className="modal-content border-0 shadow-lg p-3">
              <div className="modal-body"><i className="bi bi-exclamation-triangle text-danger display-4 mb-3 d-block"></i><p>¿Borrar #{clienteIdABorrar}?</p></div>
              <div className="modal-footer justify-content-center border-0 gap-2">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>No</button>
                <button className="btn btn-danger" onClick={confirmDelete}>Sí, borrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressBook;